import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { concat, EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute } from 'src/app/app.selectors';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { mailboxMessageChanged } from 'src/app/signalr/currently-viewing-mailbox-message-register-hub/currently-viewing-mailbox-message-register-hub.actions';
import * as ExtensionDetailActions from '../../extension-details/state/extension-detail.actions';
import * as MailboxDetailActions from '../../mailbox-details/state/mailbox-detail.actions';
import { MailboxMessageDetailApiService } from '../services/update-mailbox-message-api.service';
import * as MailboxMessageDetailActions from './mailbox-message-detail.actions';

let locallyChangedMessage = false;
@Injectable()
export class MailboxMessageDetailEffects {
   saveMailboxMessageDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(MailboxMessageDetailActions.saveMailboxMessageDetail),
         switchMap((action) => {
            const apiCall = this.mailboxMessageDetailApiService.save(
               action.mailboxMessage,
               action.mailboxMessageFields
            );
            return apiCall.pipe(
               withLatestFrom(of(action)),
               map(([result, originalAction]) => {
                  const successPayload = {
                     mailboxMessage: {
                        mailboxMessageId: result.data.mailboxMessage.mailboxMessageId,
                        isRead: originalAction.mailboxMessage.isRead,
                        isArchived: originalAction.mailboxMessage.isArchived,
                        mailboxFolderId: originalAction.mailboxMessage.mailboxFolderId,
                     },
                  };
                  return MailboxMessageDetailActions.saveMailboxMessageDetailSuccess({
                     data: successPayload as any,
                     mailboxMessageFields: originalAction.mailboxMessageFields,
                  });
               }),
               catchError((error) => {
                  return of(MailboxMessageDetailActions.saveMailboxMessageDetailFailure(error));
               })
            );
         })
      );
   });

   mailboxMessagesChanged$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(mailboxMessageChanged),
            withLatestFrom(this.store.pipe(select(selectCurrentRoute))),
            tap(([action, route]) => {
               if (!locallyChangedMessage) {
                  // TODO: if form is dirty (i.e. user is editing the extension/mailbox) should we prompt to reload so they don't lose changes?
                  if (route.url[0].path === 'extensions') {
                     this.store.dispatch(ExtensionDetailActions.loadExtensionMailbox());
                  } else if (route.url[0].path === 'mailboxes') {
                     this.store.dispatch(MailboxDetailActions.mailboxDetailLoad());
                  }
               }
               locallyChangedMessage = false;
            })
         );
      },
      { dispatch: false }
   );

   saveMailboxMessageDetailSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(MailboxMessageDetailActions.saveMailboxMessageDetailSuccess),
            tap(() => {
               locallyChangedMessage = true;
            })
         );
      },
      {
         dispatch: false,
      }
   );

   messageDetailSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(
               MailboxMessageDetailActions.deleteMailboxMessageDetailSuccess,
               MailboxMessageDetailActions.clearMailboxSuccess,
               MailboxMessageDetailActions.resetMailboxSuccess,
               MailboxMessageDetailActions.markUnreadMailboxMessageSuccess
            ),
            withLatestFrom(this.store.pipe(select(selectCurrentRoute))),
            tap(([action, route]) => {
               if (!locallyChangedMessage) {
                  if (route.url[0].path === 'extensions') {
                     this.store.dispatch(ExtensionDetailActions.loadExtensionMailbox());
                  } else if (route.url[0].path === 'mailboxes') {
                     this.store.dispatch(MailboxDetailActions.mailboxDetailLoad());
                  }
               }
               locallyChangedMessage = false;
            })
         );
      },
      {
         dispatch: false,
      }
   );

   deleteMailboxMessage = createEffect(() => {
      return this.actions$.pipe(
         ofType(MailboxMessageDetailActions.deleteMailboxMessageDetail),
         switchMap((action) => {
            const message =
               'Are you sure you would like to archive this mailbox message? The audio will be deleted permanently, only the record of the voicemail will be maintained.';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Delete Audio and Archive Message?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.mailboxMessageDetailApiService.deleteMailboxMessage(action.mailboxMessageId).pipe(
                        map(() => {
                           return MailboxMessageDetailActions.deleteMailboxMessageDetailSuccess({
                              mailboxMessageId: action.mailboxMessageId,
                           });
                        }),
                        catchError((error) => {
                           return of(MailboxMessageDetailActions.deleteMailboxMessageDetailFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   markUnreadMailboxMessage = createEffect(() => {
      return this.actions$.pipe(
         ofType(MailboxMessageDetailActions.markUnreadMailboxMessage),
         switchMap((action) =>
            this.mailboxMessageDetailApiService.markUnreadMailboxMessage(action.mailboxMessageId, action.mailboxFolderId).pipe(
               map(() =>
                  MailboxMessageDetailActions.markUnreadMailboxMessageSuccess({
                     mailboxMessageId: action.mailboxMessageId,
                  })
               ),
               catchError((error) => of(MailboxMessageDetailActions.markUnreadMailboxMessageFailure(error)))
            )
         )
      );
   });

   clearMailbox = createEffect(() => {
      return this.actions$.pipe(
         ofType(MailboxMessageDetailActions.clearMailbox),
         switchMap((action) => {
            const message =
               'Clear Mailbox will delete all the messages permanently. Are you sure you would like to clear this Mailbox?';
            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Clear Mailbox?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return concat(
                        of(MailboxMessageDetailActions.clearMailboxConfirmed()),
                        this.mailboxMessageDetailApiService.clearMailbox(action.mailboxMessageIds).pipe(
                           map(() => {
                              return MailboxMessageDetailActions.clearMailboxSuccess({
                                 mailboxMessageIds: action.mailboxMessageIds,
                              });
                           }),
                           catchError((error) => {
                              return of(MailboxMessageDetailActions.clearMailboxFailure(error));
                           })
                        )
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   resetMailbox = createEffect(() => {
      return this.actions$.pipe(
         ofType(MailboxMessageDetailActions.resetMailbox),
         switchMap((action) => {
            const message =
               'Reset mailbox will delete all the messages permanently, clear password and prompts. Are you sure you would like to reset this Mailbox?';
            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Reset Mailbox?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return concat(
                        of(MailboxMessageDetailActions.clearMailboxConfirmed()),
                        this.mailboxMessageDetailApiService
                           .resetMailbox(action.mailboxId, action.mailboxMessageIds)
                           .pipe(
                              map(() => {
                                 return MailboxMessageDetailActions.resetMailboxSuccess(action);
                              }),
                              catchError((error) => {
                                 return of(MailboxMessageDetailActions.resetMailboxFailure(error));
                              })
                           )
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   constructor(
      private actions$: Actions,
      private mailboxMessageDetailApiService: MailboxMessageDetailApiService,
      private confirmationService: ConfirmationDialogService,
      private store: Store<AppState>
   ) {}
}
